import { useEffect } from 'react';
import useWorldStore from '../store/worldStore';

const TODAY = new Date().toISOString().slice(0, 10);
const inflightSchedules = new Map();

function getCacheKey(agentId) {
  return `schedule_${agentId}_${TODAY}`;
}

function buildPrompt(agent) {
  return `Generate a daily schedule for this AI agent living in Ego-opolis, a dense cyberpunk city.

Agent profile:
Name: ${agent.name}
Role: ${agent.role}
Personality trait: ${agent.personality?.trait}
Bio: ${agent.personality?.bio}
Skills: ${agent.skills?.map(s => s.name).join(', ')}
Tags: ${agent.personality?.tags?.join(', ')}

City context — Ego-opolis districts and what's available there:
- Residential North/East/West: apartments, corner stores, quiet streets, home life
- Neon Commercial: shops, convenience stores, street food, neon-lit markets
- Recreation Quarter: gym, rooftop track, urban park, bouldering wall
- Social Hub: bars, cafés, rooftop lounges, night markets
- Transit Core: metro station, commuter routes, transit cafés

Schedule constraints:
- All activities must be plausible for a city-dweller in this environment
- No suburban or rural activities (no "hiking", "driving to the countryside", etc.)
- Activities should reflect the agent's personality and the city districts available
- A night owl agent stays out late in the Social Hub; a fitness-focused agent hits the Recreation Quarter gym at 6am
- Commuting is realistic — agents travel between districts for activities

Return JSON only with this exact shape:
{
  "weekdaySlots": [ array of slot objects ],
  "weekendSlots": [ array of slot objects ],
  "weekendActivities": [ array of activity objects ]
}

Slot object shape: { id, actId, label, emoji, winStart, winEnd, durMin, durMax, priority, isSleep }
Activity object shape: { id, name, emoji, cat, availability, dur, base, hw, sat, cost, traits }

actId must be one of: sleep | morning_routine | eating | exercise | social | leisure | work | chores
availability must be one of: weekday | weekend | any
winEnd > 24 wraps past midnight (e.g. sleep winStart:22 winEnd:30)
hw is an array of 24 numbers (hour weights) — peak hours for the activity get higher values (0–6 range)
Generate 5–7 weekday slots, 4–6 weekend slots, 2–3 weekend activities that fit their personality and the city.`;
}

async function generateScheduleForAgent(agent) {
  const cacheKey = getCacheKey(agent.id);

  // Check localStorage cache
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const schedule = JSON.parse(cached);
      if (schedule.weekdaySlots && schedule.weekendSlots) {
        return schedule;
      }
    }
  } catch {}

  const existing = inflightSchedules.get(cacheKey);
  if (existing) {
    return existing;
  }

  const request = (async () => {
    try {
      const res = await fetch('/api/schedule/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent,
          prompt: buildPrompt(agent),
        }),
      });

      if (!res.ok) {
        console.warn(`Schedule gen failed for ${agent.name}:`, res.status);
        return null;
      }

      const schedule = await res.json();

      // Validate structure
      if (!schedule.weekdaySlots || !schedule.weekendSlots) {
        console.warn(`Invalid schedule structure for ${agent.name}`);
        return null;
      }

      // Cache to localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify(schedule));
      } catch {}

      return schedule;
    } catch (err) {
      console.warn(`Schedule gen error for ${agent.name}:`, err.message);
      return null;
    } finally {
      inflightSchedules.delete(cacheKey);
    }
  })();

  inflightSchedules.set(cacheKey, request);
  return request;
}

export default function useScheduleGen() {
  const agentIds = useWorldStore((state) => state.agents.map((agent) => agent.id).join('|'));

  useEffect(() => {
    const agents = useWorldStore.getState().agents;
    if (agents.length === 0) return;
    let cancelled = false;

    // Generate schedules for all agents in parallel
    agents.forEach(async (agent) => {
      const schedule = await generateScheduleForAgent(agent);
      if (!cancelled && schedule) {
        useWorldStore.getState().setAgentSchedule(agent.id, schedule);
        console.log(`[schedule] Loaded schedule for ${agent.name}`);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [agentIds]);
}
